<template>
  <div>
    <div class="row m-4 mt-0 mb-0">
      <div class="col-sm-12">
        <div class="card card-body mb-0">
          <div class="row">
            <div class="form-group col-sm-12">
              <ul class="navbar-nav overflow-auto flex-row gap-4">
                <li v-for="value in availableTabs" :key="value" class="nav-item nav-item-spaced d-lg-block text-nowrap">
                  <a href="#" @click.prevent="selectTab(value)" class="nav-link tab" :class="{'active': activeTabName === value}">
                    {{ helper.trans(`transfers.${value}`) }}
                    <span
                      v-if="showBadges && getTabPendingCount(value) > 0"
                      class="badge bg-danger rounded-pill ms-2"
                    >{{ getTabPendingCount(value) }}</span>
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="row">
      <WithdrawalRequests v-if="availableTabs.includes(activeTabName) && activeTabName === Tabs.withdrawals"></WithdrawalRequests>
      <Deposits v-if="availableTabs.includes(activeTabName) && activeTabName === Tabs.deposits"></Deposits>
      <InternalTransferRequests v-if="availableTabs.includes(activeTabName) && activeTabName === Tabs.internal"></InternalTransferRequests>
    </div>
  </div>
</template>
<script lang="ts">
import {computed, defineComponent, onMounted, onUnmounted, reactive, toRefs} from 'vue'
import {helper} from "@/service/CommonHelper";
import router from "@/router";
import {useRoute} from "vue-router";
import WithdrawalRequests from "@/views/WithdrawalRequests.vue";
import InternalTransferRequests from "@/views/InternalTransferRequests.vue";
import Deposits from "@/views/Deposits.vue";
import {useStore} from "vuex";
import {UserPermission} from "@/models/enum/UserPermission";
import APIClient from "@/service/APIClient";
import axios from "axios";
import PendingPaymentRequestsCountDTO from "@/models/PendingPaymentRequestsCountDTO";
import {frontendSseClient} from "@/service/FrontendSseClient";

enum Tabs {
  withdrawals = 'withdrawals', deposits = 'deposits', internal = 'internal'
}

const PAYMENT_REQUESTS_COUNT_UPDATED_EVENT = 'payment_requests_count_updated';
const api = new APIClient();

export default defineComponent({
  components: {
    Deposits,
    WithdrawalRequests,
    InternalTransferRequests,
  },
  setup() {
    const route = useRoute()
    const store = useStore()
    const state = reactive({
      activeTabName: (route.params.tab ?? Tabs.withdrawals) as Tabs,
      pendingCounts: {
        withdrawals: 0,
        internalTransfers: 0,
        total: 0,
      } as PendingPaymentRequestsCountDTO,
      showBadges: false,
      sseConnected: false,
    });

    const hasPaymentsAccess = computed(() => {
      const hasTraderPayments = store.getters.hasPermission(UserPermission.PAYMENTS_TRADER_SETTLEMENTS)
      const hasClientPayments = store.getters.hasPermission(UserPermission.PAYMENTS_CLIENT_SETTLEMENTS)

      return !store.getters.isAdmin
        || (!hasTraderPayments && !hasClientPayments)
        || hasTraderPayments
        || hasClientPayments
    })

    const availableTabs = computed(() => {
      return hasPaymentsAccess.value ? Object.values(Tabs) : []
    })

    const canShowBadges = computed(() => store.getters.isAdmin || store.getters.isOperator);
    let unsubscribeSse: (() => void) | null = null;

    const applyPendingCounts = (counts: PendingPaymentRequestsCountDTO) => {
      state.pendingCounts = counts;
      state.showBadges = true;
    };

    const loadPendingCount = async () => {
      if (!canShowBadges.value) {
        return;
      }

      try {
        applyPendingCounts(await api.pendingPaymentRequestsCount());
      } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 403) {
          state.showBadges = false;
          return;
        }
      }
    };

    const connectSse = () => {
      if (!canShowBadges.value || state.sseConnected) {
        return;
      }

      unsubscribeSse = frontendSseClient.subscribe(
        PAYMENT_REQUESTS_COUNT_UPDATED_EVENT,
        (data) => applyPendingCounts(data as PendingPaymentRequestsCountDTO),
      );
      frontendSseClient.connect(() => store.getters.getAuth?.token);
      state.sseConnected = true;
    };

    const disconnectSse = () => {
      unsubscribeSse?.();
      unsubscribeSse = null;

      if (state.sseConnected) {
        frontendSseClient.disconnect();
        state.sseConnected = false;
      }
    };

    const getTabPendingCount = (tab: Tabs): number => {
      if (tab === Tabs.withdrawals) {
        return state.pendingCounts.withdrawals;
      }

      if (tab === Tabs.internal) {
        return state.pendingCounts.internalTransfers;
      }

      return 0;
    };

    const selectTab = async (value: string) => {
      router.push(`/admin/transfers/${value}`)
      state.activeTabName = value as Tabs

      if (!state.sseConnected) {
        await loadPendingCount();
      }
    };

    onMounted(async () => {
      await loadPendingCount();
      connectSse();
    });

    onUnmounted(() => {
      disconnectSse();
    });

    onMounted(() => {
      if (availableTabs.value.length > 0 && !availableTabs.value.includes(state.activeTabName)) {
        selectTab(availableTabs.value[0])
      }
    })

    return {
      ...toRefs(state),
      helper,
      Tabs,
      availableTabs,
      selectTab,
      getTabPendingCount,
      showBadges: computed(() => state.showBadges && canShowBadges.value),
    }
  }
})
</script>
